import { combineReducers, configureStore } from "@reduxjs/toolkit";
import storage from "redux-persist/lib/storage";
//import thunk from "redux-thunk";
import {
  persistStore,
  persistReducer,
  createTransform,
  createMigrate,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";

const migrations = {
  2: (state) => {
    // eslint-disable-next-line no-unused-vars
    const { sessionuser, services, codeauth, updateauth, reprint, ...rest } = state ?? {};
    return rest;
  },
  // v2→v3: elimina de localStorage los slices con datos médicos y financieros
  // que no necesitan sobrevivir a un refresh (se re-fetchean al iniciar sesión).
  3: (state) => {
    // eslint-disable-next-line no-unused-vars
    const {
      cuentaporcobrar, estadodecuenta, cartaretencion, detallespagos,
      detallesreclamo, estadodecuentas, consultarauth, detallesaut,
      pagodecomisiones, pagodecomisionespdf,
      ...rest
    } = state ?? {};
    return rest;
  },
};

// H1 OWASP A02 — Excluir el token JWT de la persistencia en localStorage.
// El token se mantiene solo en memoria: al refrescar la página el usuario
// debe volver a autenticarse, lo que impide que un atacante con acceso a
// localStorage obtenga credenciales válidas.
const userTransform = createTransform(
  // inbound: lo que se GUARDA en storage → quitamos el token
  (inboundState) => {
    // eslint-disable-next-line no-unused-vars
    const { tokenuser, token, ...safeState } = inboundState;
    return safeState;
  },
  // outbound: lo que se RESTAURA de storage → pasa sin cambios
  (outboundState) => outboundState,
  { whitelist: ["user"] }
);
import userSliceReducer, { logoutUser } from "./state/loginstate/user";
import dataSliceReducer from "./state/loginstate/data";
import nucleofamReducer from "./state/afiliadostate/nucleofam";
import medicinadispReducer from "./state/afiliadostate/medicinadisp";
import cuentaporcobrarReducer from "./state/afiliadostate/cuentaporcobrar";
import presertificacionesReducer from "./state/afiliadostate/presertificaciones";
import trackauthReducer from "./state/prestadorstate/trackauth";
import cuentaporpagarReducer from "./state/prestadorstate/cuentaporpagar";
import consultarpagosReducer from "./state/prestadorstate/consultarpagos";
import validateuserReducer from "./state/validateuser/validateuser";
import rolesReducer from "./state/roles/roles";
import estadocuentaReducer from "./state/prestadorstate/estadocuenta";
import cartaretencionReducer from "./state/prestadorstate/cartaretencion";
import detallespagosReducer from "./state/prestadorstate/detallespagos";
import detallesreclamoReducer from "./state/prestadorstate/detallesreclamo";
import lideresdeviajesReducer from "./state/promotorstate/lideresdeviajes";
import origenesReducer from "./state/promotorstate/origenes";
import destinosReducer from "./state/promotorstate/destinos";
import viajesReducer from "./state/promotorstate/viajes";
import editarviajeReducer from "./state/promotorstate/editarviaje";
import descripcionviajesReducer from "./state/promotorstate/descripcionviajes";
import guardaregistroviajesReducer from "./state/promotorstate/guardaregistroviajes";
import nombreorigenReducer from "./state/promotorstate/nombreorigen";
import nombredestinoReducer from "./state/promotorstate/nombredestino";
import registroproduccionviajesReducer from "./state/promotorstate/registroproduccionviajes";
import consultartauthReducer from "./state/prestadorstate/consultartauth";
import detallesautReducer from "./state/afiliadostate/detallesautorizaciones";
import updateperfilReducer from "./state/afiliadostate/updateperfil";
import updateSliceReducer from "./state/loginstate/update";
import trakingSliceReducer from "./state/tracking/traking";
import estadodecuentasReducer from "./state/afiliadostate/estadodecuentas";
import pagodecomisionesReducer from "./state/promotorstate/pagodecomisiones";
import pagodecomisionespdfReducer from "./state/promotorstate/pagodecomisionespdf";

const persisConfig = {
  key: "root",
  version: 3,
  storage,
  transforms: [userTransform],
  migrate: createMigrate(migrations, { debug: false }),
  // A04 OWASP — Solo se persisten slices de UI/configuración y datos de sesión
  // sin información médica ni financiera sensible. Esos slices se re-fetchean
  // al iniciar sesión, eliminando su exposición en localStorage.
  whitelist: [
    "user",
    "update",
    "validateuser",
    "lideresdeviajes",
    "origenes",
    "destinos",
    "viajes",
    "editarviaje",
    "descripcionviajes",
    "guardaregistroviajes",
    "nombreorigen",
    "nombredestino",
    "updateperfilt",
    "tracking",
  ],
};

const appReducer = combineReducers({
  user: userSliceReducer,
  data: dataSliceReducer,
  update: updateSliceReducer,
  nucleofamiliar: nucleofamReducer,
  medicinadisp: medicinadispReducer,
  cuentaporcobrar: cuentaporcobrarReducer,
  presertificaciones: presertificacionesReducer,
  trackauth: trackauthReducer,
  cuentaporpagar: cuentaporpagarReducer,
  consultarpagos: consultarpagosReducer,
  validateuser: validateuserReducer,
  roles: rolesReducer,
  estadocuenta: estadocuentaReducer,
  cartaretencion: cartaretencionReducer,
  detallespagos: detallespagosReducer,
  detallesreclamo: detallesreclamoReducer,
  lideresdeviajes: lideresdeviajesReducer,
  origenes: origenesReducer,
  destinos: destinosReducer,
  viajes: viajesReducer,
  editarviaje: editarviajeReducer,
  descripcionviajes: descripcionviajesReducer,
  guardaregistroviajes: guardaregistroviajesReducer,
  registroproduccionviajes: registroproduccionviajesReducer,
  nombreorigen: nombreorigenReducer,
  nombredestino: nombredestinoReducer,
  consultarauth: consultartauthReducer,
  detallesaut: detallesautReducer,
  updateperfilt: updateperfilReducer,
  tracking: trakingSliceReducer,
  estadodecuentas: estadodecuentasReducer,
  pagodecomisiones: pagodecomisionesReducer, // Reducer para pagos de comisiones
  pagodecomisionespdf: pagodecomisionespdfReducer, // Reducer para pagos de comisiones PDF
});

const rootReducer = (state, action) => {
  if (action.type === logoutUser.type) {
    state = undefined; // reset total
  }
  return appReducer(state, action);
};

const persistedReducer = persistReducer(persisConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: import.meta.env.DEV,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      thunk: true, // necesario para createAsyncThunk
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export default rootReducer;